import { reportLabel } from "@/lib/api";
import { ClipboardList, Plus, PackageCheck } from "lucide-react";
import { fmtDate } from "@/lib/dateFormat";

/**
 * OrderHistoryCard — right-column Supporting Intelligence surface that
 * displays the audit-safe order lifecycle for an application.
 *
 * Populated from `screening.order_history[]`, which the backend seeds on
 * every screening create ("order_placed") and appends to on every
 * services-added event. This gives PMs and NTN Staff a chronological
 * "who added what and when" view without needing to open the message log.
 */
export default function OrderHistoryCard({ screening, testid = "order-history" }) {
  const history = screening.order_history || [];
  if (history.length === 0) return null;

  const original = history.find((h) => h.event === "order_placed");
  const additions = history.filter((h) => h.event === "services_added");

  return (
    <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm overflow-hidden" data-testid={testid}>
      <header className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
          <ClipboardList className="h-4 w-4 text-slate-600" />
        </div>
        <div className="min-w-0">
          <div className="ntn-eyebrow">Supporting Intelligence · Case Context</div>
          <div className="ntn-h3 mt-0.5">Order History</div>
        </div>
      </header>

      <div className="divide-y divide-slate-100">
        {original && (
          <div className="px-5 py-3.5" data-testid={`${testid}-original`}>
            <div className="flex items-center gap-2 mb-1">
              <PackageCheck className="h-3.5 w-3.5 text-slate-500 shrink-0" />
              <div className="ntn-eyebrow">Original Order</div>
              <div className="ml-auto ntn-mono">
                {fmtDate(original.at)}
              </div>
            </div>
            {original.package === "decision_ready" && (
              <div className="ntn-h4 font-serif">⭐ Decision-Ready Screening Package</div>
            )}
            <ul className="mt-1.5 flex flex-wrap gap-1">
              {(original.services || []).map((svc) => (
                <li key={svc} className="ntn-mono uppercase px-1.5 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-200">
                  {reportLabel(svc)}
                </li>
              ))}
            </ul>
            <div className="ntn-mono mt-2 uppercase">
              Placed by {original.added_by_name || "—"}
            </div>
          </div>
        )}

        {additions.map((h) => (
          <div key={h.id} className="px-5 py-3.5" data-testid={`${testid}-addition-${h.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <Plus className="h-3.5 w-3.5 text-amber-600 shrink-0" />
              <div className="ntn-eyebrow text-amber-700">Services Added</div>
              <div className="ml-auto ntn-mono">
                {fmtDate(h.at)}
              </div>
            </div>
            <ul className="flex flex-wrap gap-1">
              {(h.services || []).map((svc) => (
                <li key={svc} className="ntn-mono uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                  {reportLabel(svc)}
                </li>
              ))}
            </ul>
            <div className="ntn-mono mt-2 uppercase">
              Added by {h.added_by_name || "—"} · {h.added_by_role === "ntn_staff" ? "NTN Staff" : "Property Manager"}
            </div>
            {h.reason && (
              <div className="ntn-secondary italic mt-1.5 bg-slate-50 border-l-2 border-slate-300 pl-2 py-1">
                “{h.reason}”
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
